'use client';

import React from 'react';
import styled from '@benzinga/themetron';
import Portal from '../../utils/Portal';
import TooltipPopover from './TooltipPopover';
import { useClickOutside } from '@benzinga/hooks';
import { computeTooltipGeometry, TooltipPlacement } from './geometry';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export interface TooltipProps {
  className?: string;
  content?: React.ReactNode;
  delay?: number;
  maxWidth?: number;
  minWidth?: number;
  width?: number;
  visible?: boolean;
  trigger?: 'hover' | 'click';
  size?: 'sm' | 'md' | 'lg';
  space?: number;
  position?: TooltipPlacement;
  isContentHtml?: boolean;
  onShow?: () => void;
  onHide?: () => void;
}

export const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> = React.memo(
  ({
    children,
    className = '',
    content,
    delay = 0,
    isContentHtml = false,
    maxWidth = 500,
    minWidth = 120,
    onHide,
    onShow,
    position,
    size = 'md',
    space = 10,
    trigger = 'hover',
    visible,
    width,
  }) => {
    const [isVisible, setIsVisible] = React.useState(!!visible);
    const [style, setStyle] = React.useState<React.CSSProperties | undefined>({});
    const [tooltipArrowStyle, setTooltipArrowStyle] = React.useState<{ [key: string]: string } | undefined>({});
    const containerRef = React.useRef<HTMLDivElement>(null);
    const tooltipPopoverRef = React.useRef<HTMLDivElement>(null);
    const animationFrameRef = React.useRef<number>(0);
    const hideTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const isControlled = React.useMemo(() => typeof visible !== 'undefined', [visible]);

    const currentPlacement = React.useRef<TooltipPlacement>(position ?? 'top');

    React.useEffect(() => {
      if (isControlled) {
        setIsVisible(!!visible);
      }
    }, [isControlled, visible]);

    useClickOutside(containerRef, (event?: MouseEvent) => {
      if (!event) {
        if (!isControlled) setIsVisible(false);
        return;
      }
      if (
        trigger === 'click' &&
        !visible &&
        tooltipPopoverRef?.current &&
        !tooltipPopoverRef.current.contains(event.target as Node)
      ) {
        if (!isControlled) setIsVisible(false);
      }
    });

    const updateTooltipCoords = React.useCallback(() => {
      if (!isVisible) return;
      const anchorRect = containerRef.current?.getBoundingClientRect();
      const popoverRect = tooltipPopoverRef.current?.getBoundingClientRect();
      if (!anchorRect || !popoverRect) return;

      const geometry = computeTooltipGeometry({
        anchorRect,
        maxWidth,
        minWidth,
        popoverRect,
        preferred: position,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        space,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
        width,
      });

      currentPlacement.current = geometry.placement;
      setStyle(geometry.tooltip);
      setTooltipArrowStyle(geometry.tooltipArrow);
    }, [isVisible, maxWidth, minWidth, position, space, width]);

    const scheduleUpdate = React.useCallback(() => {
      if (!isVisible) return;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateTooltipCoords);
    }, [isVisible, updateTooltipCoords]);

    const forceInitialUpdate = React.useCallback(() => {
      if (!isVisible) return;
      requestAnimationFrame(() => {
        scheduleUpdate();
        requestAnimationFrame(scheduleUpdate);
      });
    }, [isVisible, scheduleUpdate]);

    React.useEffect(() => {
      if (isVisible) {
        scheduleUpdate();
      }
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [isVisible, scheduleUpdate]);

    React.useEffect(() => {
      if (isVisible) {
        forceInitialUpdate();
      }
    }, [content, forceInitialUpdate, isVisible, maxWidth, minWidth, position, size, space, width]);

    React.useEffect(() => {
      if (!isVisible) return;
      const handleWindowResize = () => scheduleUpdate();
      const handleWindowScroll = () => scheduleUpdate();
      window.addEventListener('resize', handleWindowResize, { passive: true });
      window.addEventListener('scroll', handleWindowScroll, true);
      return () => {
        window.removeEventListener('resize', handleWindowResize);
        window.removeEventListener('scroll', handleWindowScroll, true);
      };
    }, [isVisible, scheduleUpdate]);

    React.useEffect(() => {
      if (!isVisible || typeof ResizeObserver === 'undefined') return;
      const containerNode = containerRef.current;
      const popoverNode = tooltipPopoverRef.current;
      if (!containerNode || !popoverNode) return;

      const observer = new ResizeObserver(() => scheduleUpdate());
      observer.observe(containerNode);
      observer.observe(popoverNode);

      return () => {
        observer.disconnect();
      };
    }, [isVisible, scheduleUpdate]);

    const showTooltip = React.useCallback(() => {
      if (!isControlled) {
        setIsVisible(true);
      }
      if (onShow) onShow();
    }, [isControlled, onShow]);

    const hideTooltip = React.useCallback(() => {
      if (!isControlled) {
        setIsVisible(false);
      }
      if (onHide) onHide();
    }, [isControlled, onHide]);

    const debouncedHideTooltip = React.useCallback(() => {
      if (!delay) {
        hideTooltip();
        return;
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = setTimeout(hideTooltip, delay);
    }, [delay, hideTooltip]);

    const handleToggleTooltipOnClick = React.useCallback(() => {
      if (isVisible) hideTooltip();
      else showTooltip();
    }, [hideTooltip, isVisible, showTooltip]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
          hideTooltip();
        }
      },
      [hideTooltip],
    );

    React.useEffect(() => {
      return () => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      };
    }, []);

    return (
      <Container
        aria-expanded={isVisible}
        aria-haspopup="dialog"
        className={`tooltip-container ${trigger}`}
        id="tooltip-container"
        onBlur={trigger === 'click' ? undefined : debouncedHideTooltip}
        onClick={trigger === 'hover' ? undefined : handleToggleTooltipOnClick}
        onFocus={trigger === 'click' ? undefined : showTooltip}
        onKeyDown={handleKeyDown}
        onMouseEnter={trigger === 'click' ? undefined : showTooltip}
        onMouseLeave={trigger === 'click' ? undefined : debouncedHideTooltip}
        ref={containerRef}
      >
        {children}
        {isVisible && (
          <Portal>
            {content && (
              <TooltipPopover
                className={className}
                placement={currentPlacement.current}
                ref={tooltipPopoverRef}
                size={size}
                style={style}
                tooltipArrowStyle={tooltipArrowStyle}
              >
                {isContentHtml ? <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(String(content)) }} /> : content}
              </TooltipPopover>
            )}
          </Portal>
        )}
      </Container>
    );
  },
);

export default Tooltip;

const Container = styled.div`
  &.tooltip-container {
    display: inline-flex;
    position: relative;
    align-items: center;

    &.click {
      cursor: pointer;
    }
  }
`;
